import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    setFocus,
    setRef,
    hasFailedInstantMatch,
    isFormFilled,
    VerificationStepsEnum,
    MedicalProfessionalStatusDefaultMessagesEnum,
    VerificationService,
    FormSelectChoice,
    Organization,
    PhoneNumberComponent as PhoneNumber,
    InputSelectOnKeyDown,
    MedicalProfessionalPersonalInfoViewModel,
    MedicalProfessionalPersonalInfoResponse,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    FormFooterComponent as FormFooter,
    MarketConsentWrapper,
    RewardsRemainingComponent,
} from '@sheerid/jslib';

import { FirstNameComponent as FirstName } from '../../FormFields/FirstNameComponent';
import { LastNameComponent as LastName } from '../../FormFields/LastNameComponent';
import { EmailComponent as Email } from '../../FormFields/EmailComponent';
import { BirthDateComponent as BirthDate } from '../../FormFields/BirthDateComponent';
import { PostalCodeComponent } from '../../FormFields/PostalCodeComponent';
import { MedicalProfessionalOrganizationComponent as MedicalProfessionalOrganization } from '../../FormFields/MedicalProfessionalOrganizationComponent';
import { MedicalStatusComponent } from '../../FormFields/MedicalStatusComponent';


interface StepMedicalProfessionalPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

export const StepMedicalProfessionalPersonalInfo = ({ intl, verificationService }: StepMedicalProfessionalPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;
    const verificationResponse = verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateMedicalProfessionalViewModel = (key: keyof MedicalProfessionalPersonalInfoViewModel, value: any) => {
        const nextState: MedicalProfessionalPersonalInfoViewModel = produce(viewModel, (draft: MedicalProfessionalPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const getAvailableMedicalStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse = (verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MedicalProfessionalStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    const handleMedicalStatusOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    return (
        <div id="sid-step-active-medical-professional-personal-info" className="sid-l-container">
            <img className="sid-header__logo" src="https://assets-resources.sheerid.com/jcrew/img/j.crew_logo_2022.png" alt="" />
            { failedInstantMatch
                ? (
                    <div className="sid-header">
                        <div className="sid-l-horz-center" />
                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name, Email & Organization" />
                        </div>
                        <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                            <FormattedHTMLMessage
                                id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name, organization name, and email must match what is on record with your agency."
                            />
                        </div>
                    </div>
                )
                : (
                    <div className="sid-header">
                        <div className="sid-header__subtitle-info sid-l-horz-center">
                            <FormattedHTMLMessage
                                id="step.personalInfo.subtitle"
                                defaultMessage="Complete the college student form below to confirm your eligibility."
                            />
                        </div>

                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student-Only Offer" />
                        </div>


                        <div className="sid-header__how-verifying-works sid-l-horz-center">
                            <RewardsRemainingComponent verificationService={verificationService} />
                        </div>
                    </div>
                )}
            <div className="sid-sub-header-all-fields-text">
                <FormattedHTMLMessage
                    id="requiredFields"
                />
            </div>
            <MedicalStatusComponent
                value={{
                    value: (verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).status,
                    label: MedicalProfessionalStatusDefaultMessagesEnum[(verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={getAvailableMedicalStatuses()}
                placeholder={
                    intl.formatMessage({
                        id: 'statusSelect',
                        defaultMessage: 'Status',
                    })
                }
                onChange={
                    (status: FormSelectChoice) => {
                        updateMedicalProfessionalViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                        if (status) {
                            setFocus('organization');
                        }
                    }
                }
                onKeyDown={event => handleMedicalStatusOnKeyDown(event)}
            />

            <MedicalProfessionalOrganization
                isErrored={!!fieldValidationErrors.organization}
                placeholder={
                    intl.formatMessage({
                        id: 'organization',
                        defaultMessage: 'Organization',
                    })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: 'firstName',
                            defaultMessage: 'First name',
                        })
                    }
                    onChange={
                        (newValue: string) => {
                            updateMedicalProfessionalViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: 'lastName',
                            defaultMessage: 'Last name',
                        })
                    }
                    onChange={
                        (newValue: string) => {
                            updateMedicalProfessionalViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateMedicalProfessionalViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <PostalCodeComponent
                isErrored={!!fieldValidationErrors.postalCode}
                placeholder={
                    intl.formatMessage({
                        id: 'postalCode',
                        defaultMessage: 'Postal Code',
                    })
                }
                onChange={(newValue: string) => {
                    updateMedicalProfessionalViewModel('postalCode', newValue);
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.postalCode}
                explanation={(
                    <FormattedHTMLMessage
                        id="postalCodeExplanation"
                        defaultMessage="Use the location on file with your licensing body"
                    />
                )}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: 'email',
                        defaultMessage: 'Email address',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateMedicalProfessionalViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled
                    ? (
                        <PhoneNumber
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            onChange={
                                (newValue: string) => {
                                    updateMedicalProfessionalViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    )
                    : null
            }
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-privacy-policy-container">
                <span>
                    <FormattedHTMLMessage
                        id="jcrewPrivacyPolicy"
                    />
                </span>
            </div>

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectMedicalProfessionalPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        { failedInstantMatch
                            ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            : <FormattedHTMLMessage id="verifyMyMedicalProfessionalStatus" defaultMessage="Verify my medical professional status" />}
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="firstResponderInfoShared"
                />
                {
                    viewModel.countryChoice && viewModel.countryChoice.value !== 'US'
                        ? (
                            <div>
                                <FormattedHTMLMessage
                                    id="informationTransferredToUS"
                                    defaultMessage="Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                                />

                            </div>
                        )

                        : null
                }

                <div className="sid-last-footer">
                    <span className="sid-footer-one">
                        <FormattedHTMLMessage
                            id="lastFooter"
                        />
                    </span>

                    <p className="sid-footer-two">
                        <FormattedHTMLMessage
                            id="footerOne"
                        />
                        <FormattedHTMLMessage
                            id="footerTwo"
                        />
                    </p>
                </div>
            </FormFooter>
        </div>
    );
};

export const StepMedicalProfessionalPersonalInfoComponent = injectIntl(StepMedicalProfessionalPersonalInfo);
